import CaretDown from '@mds/icons/CaretDown';
import CaretRight from '@mds/icons/CaretRight';
import { iconSizes } from '@mds/tokens';
import { hasRenderableNode } from '@mds/ui';
import { overlay } from '@mds/ui/theme/styleOverrides/tabs';
import { themeVars } from '@mds/ui/theme/themeVars';
import {
  Collapse,
  ExtendButtonBase,
  ListItemButton,
  listItemButtonClasses,
  ListItemButtonProps,
  ListItemButtonTypeMap,
  listItemIconClasses,
  styled,
  svgIconClasses,
  type CollapseProps,
} from '@mui/material';
import { forwardRef, MouseEvent, ReactNode, useEffect, useState } from 'react';

const navigationLineWidth = '1px';

const StyledButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'disableInteractionStyles',
})<{
  disableInteractionStyles?: boolean;
  selected?: boolean;
}>(({ theme }) => ({
  ...theme.mixins.gutterVariables,
  padding: theme.spacing(1),
  minHeight: '40px',
  width: '100%',
  // Minimum width could cause inconsistent spacing between indicator and text
  minWidth: 'auto',
  position: 'relative',
  zIndex: 0,
  backgroundColor: 'transparent',

  [`& .${listItemIconClasses.root} .${svgIconClasses.root}`]: {
    fontSize: iconSizes.lg,
  },

  '&:before': {
    ...overlay,
    top: 'var(--app-navigation-line-top, 0)',
    bottom: 'var(--app-navigation-line-bottom, 0)',
    left: 'var(--app-navigation-line-left, 0px)',
    width: 'var(--app-navigation-line-width, 0px)',
    height: 'var(--app-navigation-line-height, auto)',
    transform: 'var(--app-navigation-line-transform, none)',
    backgroundColor: 'var(--app-navigation-line-color, transparent)',
    zIndex: 1,
    transition: theme.transitions.create(['background-color', 'width', 'height'], {
      duration: theme.transitions.duration.standard,
    }),
  },

  '&:after': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 'var(--app-navigation-highlight-inset, 0px)',
    zIndex: -1,
    backgroundColor: 'transparent',
    pointerEvents: 'none',
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
  },

  '&:hover': {
    backgroundColor: 'transparent',
    '--app-navigation-line-color': 'var(--app-navigation-hover-line-color, transparent)',
    '--app-navigation-line-width': 'var(--app-navigation-hover-line-width, 0px)',
    '&:after': {
      backgroundColor: themeVars(theme).palette.action.hover,
    },
    '@media (hover: none)': {
      '&:after': {
        backgroundColor: 'transparent',
      },
    },
  },

  '&:active': {
    backgroundColor: 'transparent',
    '&:after': {
      backgroundColor: themeVars(theme).palette.action.pressed,
    },
  },

  [`&.${listItemButtonClasses.focusVisible}`]: {
    background: 'none',
    '&:after': {
      backgroundColor: themeVars(theme).palette.action.hover,
    },
  },

  variants: [
    {
      props: { selected: true },
      style: ({ theme }) => ({
        '&, &:hover, &:active, &:focus-visible': {
          backgroundColor: 'transparent',
          '--app-navigation-line-color': 'var(--app-navigation-selected-line-color, transparent)',
          '--app-navigation-line-width': 'var(--app-navigation-selected-line-width, 0px)',
        },
        [`&:after, &:hover:after, &:focus-visible:after, &.${listItemButtonClasses.focusVisible}:after`]: {
          backgroundColor: themeVars(theme).palette.action.selected,
          borderRadius: theme.vars?.shape?.borderRadius ?? theme.shape.borderRadius,
        },
        '&:active:after': {
          backgroundColor: themeVars(theme).palette.action.pressed,
        },
      }),
    },
    {
      props: { disableInteractionStyles: true },
      style: {
        '&, &:hover, &:active, &:focus-visible': {
          backgroundColor: 'transparent',
          '--app-navigation-line-color': 'transparent',
          '--app-navigation-line-width': '0px',
        },
        [`&:after, &:hover:after, &:focus-visible:after, &.${listItemButtonClasses.focusVisible}:after`]: {
          backgroundColor: 'transparent',
        },
      },
    },
  ],
}));

const CollapseContent = styled('div')(({ theme }) => ({
  '--app-collapse-parent-icon-center': `calc(${theme.spacing(2.5)} - 3px)`,
  position: 'relative',
  padding: theme.spacing(0, 0, 0, 1),

  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 'var(--app-collapse-parent-icon-center)',
    width: navigationLineWidth,
    backgroundColor: themeVars(theme).palette.border.subtle,
    pointerEvents: 'none',
    zIndex: 0,
  },
}));

const CollapseItems = styled('div')(({ theme }) => ({
  '--app-navigation-line-left': `calc(var(--app-collapse-parent-icon-center) - ${theme.spacing(3)} - ${theme.spacing(0.5)})`,
  '--app-navigation-hover-line-color': themeVars(theme).palette.primary.border,
  '--app-navigation-hover-line-width': navigationLineWidth,
  '--app-navigation-selected-line-color': themeVars(theme).palette.primary.border,
  '--app-navigation-selected-line-width': navigationLineWidth,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  paddingLeft: `calc(${theme.spacing(2.5)})`,
  position: 'relative',
  zIndex: 1,

  [`& .${listItemButtonClasses.root}`]: {
    ...theme.typography.body2,
    minHeight: '36px',
    letterSpacing: 0,
  },
}));

const ExpandIcon = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: 'auto',
  color: themeVars(theme).palette.text.secondary,

  [`& .${svgIconClasses.root}`]: {
    fontSize: iconSizes.lg,
  },
}));

export interface SelectableListItemButtonOwnProps {
  disableInteractionStyles?: boolean;
  selected?: boolean;
  textColor?: unknown;
  collapse?: ReactNode;
  defaultExpanded?: boolean;
  slotProps?: {
    collapse?: Partial<CollapseProps>;
  };
}

export const SelectableListItemButton = forwardRef<
  HTMLDivElement,
  ListItemButtonProps & SelectableListItemButtonOwnProps
>(function SelectableListItemButton(props, ref) {
  const {
    children,
    collapse,
    defaultExpanded = false,
    onClick,
    slotProps,
    textColor: _textColor,
    ...buttonProps
  } = props;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const hasCollapse = hasRenderableNode(collapse);

  useEffect(() => {
    if (defaultExpanded) {
      // PlainNavigationItem also uses this prop to expand a parent when its route becomes active after mount.
      // eslint-disable-next-line react-hooks-extra/no-direct-set-state-in-use-effect
      setIsExpanded(true);
    }
  }, [defaultExpanded]);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsExpanded((previous) => !previous);
    onClick?.(event);
  };

  return (
    <>
      <StyledButton
        divider={false}
        {...buttonProps}
        {...(hasCollapse ? { 'aria-expanded': isExpanded } : {})}
        onClick={hasCollapse ? handleClick : onClick}
        ref={ref}
      >
        {children}

        {hasCollapse && <ExpandIcon>{isExpanded ? <CaretDown /> : <CaretRight />}</ExpandIcon>}
      </StyledButton>

      {hasCollapse && (
        <Collapse timeout="auto" unmountOnExit {...slotProps?.collapse} in={isExpanded}>
          <CollapseContent>
            <CollapseItems>{collapse}</CollapseItems>
          </CollapseContent>
        </Collapse>
      )}
    </>
  );
}) as ExtendButtonBase<ListItemButtonTypeMap<SelectableListItemButtonOwnProps>>;
